import { tk } from './i18n.js';
// Wallet sign-in: a browser wallet (MetaMask, Rabby, Coinbase…) or a local demo wallet for testing.
import { api, setToken } from './api.js';
import { privateKeyToAddress, randomPrivateKey, signPersonalMessage } from '../../server/lib/eth.js';

export const hasBrowserWallet = () => typeof window !== 'undefined' && Boolean(window.ethereum);

async function finish(address, sign) {
  const { message, nonce } = await api('POST', '/api/auth/nonce', { address });
  const signature = await sign(message);
  let ref = null;
  try { ref = localStorage.getItem('ref'); } catch { /* ignore */ }
  const res = await api('POST', '/api/auth/verify', { address, nonce, signature, ref });
  setToken(res.token);
  return res.user;
}

export async function signInWithBrowserWallet() {
  if (!hasBrowserWallet()) throw new Error(tk('No wallet found. Install MetaMask or another browser wallet.'));
  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
  const address = accounts && accounts[0];
  if (!address) throw new Error(tk('Wallet did not share an address'));
  return finish(address, async (message) => {
    const hex = '0x' + Array.from(new TextEncoder().encode(message), (b) => b.toString(16).padStart(2, '0')).join('');
    return window.ethereum.request({ method: 'personal_sign', params: [hex, address] });
  });
}

export async function signInWithDemoWallet() {
  let key = null;
  try { key = localStorage.getItem('demoKey'); } catch { /* ignore */ }
  if (!key) { key = randomPrivateKey(); try { localStorage.setItem('demoKey', key); } catch { /* ignore */ } }
  const address = privateKeyToAddress(key);
  return finish(address, async (message) => signPersonalMessage(key, message));
}

// ---- Mobile: open this site inside a wallet app's browser (that's how a dapp works on phones).
export const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
export function walletDeepLinks() {
  const url = location.href;
  const hostPath = url.replace(/^https?:\/\//, '');
  return [
    { name: 'MetaMask', href: `https://metamask.app.link/dapp/${hostPath}` },
    { name: 'Trust Wallet', href: `https://link.trustwallet.com/open_url?coin_id=60&url=${encodeURIComponent(url)}` },
    { name: 'Coinbase Wallet', href: `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(url)}` },
  ];
}

// ---- Stablecoin payment from the connected browser wallet.
// Switches to the right chain, sends an ERC-20 transfer(treasury, amount) and returns the transaction hash.
export async function sendStablecoin({ chain, tokenAddress, treasury, amountUnits }) {
  if (!hasBrowserWallet()) throw new Error(tk('Connect a browser wallet (MetaMask, Rabby, Trust…) to pay. The demo wallet cannot pay.'));
  const eth = window.ethereum;
  const [from] = await eth.request({ method: 'eth_requestAccounts' });
  const chainHex = '0x' + Number(chain.id).toString(16);
  try {
    await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chainHex }] });
  } catch (e) {
    if (e.code !== 4902) throw e;
    await eth.request({
      method: 'wallet_addEthereumChain',
      params: [{ chainId: chainHex, chainName: chain.name, rpcUrls: [chain.rpc], blockExplorerUrls: [chain.explorer],
        nativeCurrency: { name: chain.native, symbol: chain.native, decimals: 18 } }],
    });
  }
  const pad = (hex) => hex.replace(/^0x/, '').toLowerCase().padStart(64, '0');
  const data = '0xa9059cbb' + pad(treasury) + pad(BigInt(amountUnits).toString(16));
  return eth.request({ method: 'eth_sendTransaction', params: [{ from, to: tokenAddress, data, value: '0x0' }] });
}
